<script setup lang="ts">
import type { MenuItem } from '@/api'
import { useLayout } from '@/composables/layout'
import { getMatchingParameterNames, isRequireApprovalExhibitor, isRequireExhibitorId, type MenuRoute } from '@/router/menu'
import { useExhibitorStore } from '@/stores/exhibitor'
import { useFairStore } from '@/stores/fair'
import { useSiteStore } from '@/stores/site'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const siteStore = useSiteStore()
const fairStore = useFairStore()
const exhibitorStore = useExhibitorStore()
const { locale } = useI18n()

const { layoutState, setActiveMenuItem, onMenuToggle } = useLayout()

interface Props {
  item: MenuItem
  index: number
  parentItem?: MenuItem
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  parentItem: undefined
})

const isActiveMenu = ref(false)
const root = computed(() => !props.item.parentId)

function isActiveMenuItem(item: MenuItem, activeItem: MenuItem) {
  return activeItem?.id === item.id || activeItem?.parentId === item.id
}

onBeforeMount(() => {
  const activeItem = layoutState.activeMenuItem
  if (activeItem !== undefined) {
    isActiveMenu.value = isActiveMenuItem(props.item, activeItem as MenuItem)
  }
})

watch(
  () => layoutState.activeMenuItem as MenuItem,
  (newVal: MenuItem) => {
    isActiveMenu.value = isActiveMenuItem(props.item, newVal)
  }
)

function itemClick(event: Event, item: MenuItem) {
  if ((item.to || item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
    onMenuToggle()
  }

  setActiveMenuItem(item)
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }, 100)
}

function parseRoute(menuItem: MenuItem) {
  const matchingParamNames = getMatchingParameterNames(menuItem.to as string)
  const route: MenuRoute | any = {
    name: menuItem.to as string,
    params: {}
  }

  for (const paramName of matchingParamNames) {
    if (menuItem.to === 'consult-visitors' && paramName === 'FAIR_CONSULT_ID') {
      route.params.FAIR_CONSULT_ID = (menuItem.parameters as any).FAIR_CONSULT_ID
    } else if (menuItem.to === 'consult-visitors' && paramName === 'FAIR_ID') {
      route.params.FAIR_ID = (menuItem.parameters as any).FAIR_ID
    } else if (paramName === 'FAIR_ID') {
      route.params.FAIR_ID = fairStore.fair?.id
    }
  }

  return route
}

function getMenuLabel(item: MenuItem) {
  return locale.value === 'en' ? item.labelEng : item.label
}

function isDisabledRoute(item: MenuItem) {
  return (
    (isRequireApprovalExhibitor(item) && !exhibitorStore.exhibitor.isCompleteApproval()) ||
    (isRequireExhibitorId(item) && exhibitorStore.exhibitor.isEmpty())
  )
}
</script>

<template>
  <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
    <div v-if="root" class="layout-menuitem-root-text">
      {{ getMenuLabel(item) }}
    </div>
    <!-- 외부 링크 -->
    <a v-if="!item.to" :href="item.url" :target="item.target ?? '_blank'" tabindex="0" @click="itemClick($event, item)">
      <i :class="item.icon" class="layout-menuitem-icon"></i>
      <span class="layout-menuitem-text">{{ getMenuLabel(item) }}</span>
      <i v-if="item.items !== undefined && item.items.length > 0" class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
    </a>
    <!-- 내부 링크 -->
    <RouterLink
      v-if="item.to"
      :target="item.target && !isDisabledRoute(item) ? item.target : '_self'"
      :class="{ 'active-route': item == siteStore.currentMenu, 'disabled-route': isDisabledRoute(item) }"
      tabindex="0"
      :to="parseRoute(item)"
      @click="itemClick($event, item)">
      <i :class="item.icon" class="layout-menuitem-icon"></i>
      <span class="layout-menuitem-text">{{ getMenuLabel(item) }}</span>
    </RouterLink>
    <Transition v-if="item.items" name="layout-submenu">
      <ul v-show="root || isActiveMenu" class="layout-submenu">
        <AppMenuItem v-for="(child, i) in item.items" :key="child.id" :index="i" :item="child" :parent-item="item" :root="false"></AppMenuItem>
      </ul>
    </Transition>
  </li>
</template>

<style lang="postcss" scoped>
.layout-menuitem-text {
  line-height: 1.2;
}

.disabled-route {
  cursor: not-allowed;
  color: #9f9f9f;
}

.disabled-route:hover {
  cursor: not-allowed;
  color: var(--p-primary-300);
}
</style>

<i18n lang="yaml">
en:
  menu:
    disabledRoute: 'You need to complete the exhibitor application to use the menu'
    checkExhibitorApplication: 'You need to complete the exhibitor application to use the menu.\nDo you want to go to the application page?'
ko:
  menu:
    disabledRoute: '참가기업 신청을 마쳐야 메뉴를 사용하실 수 있습니다'
    checkExhibitorApplication: '참가기업 신청을 마쳐야 메뉴를 사용하실 수 있습니다.\n신청 페이지로 이동하시겠습니까?'
</i18n>
